import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

/**
 * ActivityGlyph — el latido del indicador de actividad.
 *
 * Un destello de cuatro puntas que gira despacio y pulsa, en la línea del glifo que
 * Claude Code pone delante de sus verbos de estado. Va SVG inline a propósito: un
 * emoji (✳/✻) cambia de forma, de peso y de alineación vertical según la fuente y el
 * sistema, y aquí el glifo es el elemento que comunica «sigo vivo» — no puede
 * renderizarse distinto en cada máquina.
 *
 * Sustituye a los tres puntos: glifo animado + puntos animados es redundancia, y el
 * glifo además deja hueco a la izquierda para el icono de la herramienta.
 *
 * Decorativo por definición: `aria-hidden`. Lo que un lector de pantalla debe leer es
 * el texto del estado, que va en el contenedor con `role="status"` / `aria-live`.
 *
 * @param {number} [size=14] - Lado en px.
 * @param {string} [color] - Color CSS; por defecto hereda con `currentColor`.
 */
const ActivityGlyph = ({ size = 14, color }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      color: color || 'inherit',
      transformOrigin: 'center',
      // 1,1 s por vuelta (elegido comparando 2,8 / 1,8 / 1,1 en vivo). Y a propósito NO
      // es múltiplo de los 1,4 s del pulso: si los dos ciclos coincidieran, el glifo
      // haría siempre el mismo gesto en el mismo punto del giro y se leería como un tic
      // repetitivo en vez de movimiento vivo. Evitar 1,4 y 2,8 al retocar esto.
      animation: 'glyphSpin 1.1s linear infinite, glyphPulse 1.4s ease-in-out infinite',
      '@keyframes glyphSpin': {
        from: { transform: 'rotate(0deg)' },
        to: { transform: 'rotate(360deg)' },
      },
      '@keyframes glyphPulse': {
        '0%, 100%': { opacity: 0.45 },
        '50%': { opacity: 1 },
      },
      // Accesibilidad: sin movimiento, el glifo se queda quieto y legible.
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
        opacity: 0.75,
      },
    }}
  >
    <path
      d="M12 1.8c.55 5.2 1.15 6.75 10.2 10.2-9.05 3.45-9.65 5-10.2 10.2-.55-5.2-1.15-6.75-10.2-10.2C10.85 8.55 11.45 7 12 1.8z"
      fill="currentColor"
    />
  </Box>
);

ActivityGlyph.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string,
};

export default ActivityGlyph;
